/**
 * O avatar de um contato.
 *
 * ## A foto EXISTE desde 2026-08-16, e a cobertura é parcial de propósito
 *
 * O owner pediu *"as fotos do contato igual é no whatsapp web e no chatpro"*, e o caminho foi
 * construído: migration `0034`, `GET /api/painel/contatos/:id/foto` servindo por proxy, e a consulta
 * ao WhatsApp feita sob demanda pelo processo que tem o socket.
 *
 * **Mas só o WhatsApp por aparelho vinculado alimenta isso**, e é o único ponto que precisa ficar
 * escrito aqui: a API oficial da Meta **não expõe foto de contato** — o `contacts[]` do webhook traz
 * `profile.name` e `wa_id`, e não há endpoint para o resto. Não é implementação faltando; é campo
 * que não existe do outro lado. O ChatPro e o WhatsApp Web mostram fotos porque rodam por aparelho
 * vinculado, onde elas vêm junto. É de onde o dado vem, não de como a tela é feita.
 *
 * ## Por isso o fallback continua sendo primeira classe, e não estado de carregamento
 *
 * Contato sem foto é o caso comum e **permanente** para quem usa o oficial — e frequente no não
 * oficial, porque muita gente esconde a foto na privacidade. Um esqueleto girando ali seria a tela
 * prometendo um rosto que nunca vem.
 *
 * O fallback é a inicial sobre uma cor **derivada do identificador**. Determinística — o mesmo
 * contato tem sempre a mesma cor, e é isso que faz a lista ser varrida por cor antes de ser lida.
 */

import { useState } from "react";
import { coresDaEtiqueta, superficiesDoTema, type ParDeCores } from "./contraste.js";

/**
 * O endereço da foto de um contato. **Um lugar só**, e é por isso que ele mora aqui.
 *
 * Três telas montam avatar (a lista do inbox, o cabeçalho do fio e a de Contatos), em dois builds
 * diferentes. Com o caminho escrito nas três, o dia em que a rota mudar é o dia em que duas mudam e
 * uma fica para trás — mostrando avatar quebrado numa tela só, que é o defeito que ninguém liga à
 * causa.
 *
 * Devolve `null` sem id, e **`null` e não `undefined`** por causa do `exactOptionalPropertyTypes` do
 * repositório: passar `undefined` explicitamente a uma prop opcional é erro de tipo aqui. Também
 * evita `<img src="">`, que faz o navegador **requisitar a página atual** e cair no `onError` — o
 * avatar funcionaria por acidente, deixando um pedido estranho no log do servidor.
 */
export function urlDaFotoDoContato(contatoId: string | null | undefined): string | null {
  return contatoId ? `/api/painel/contatos/${encodeURIComponent(contatoId)}/foto` : null;
}

/**
 * As cores do fallback — os NOMES dos tokens, e nada de valor.
 *
 * A primeira versão trazia os seis hexes copiados do `vidro.css`, e eram seis lugares para o dia em
 * que a palheta mudasse; a segunda trazia referências `var()` e pintava a inicial de **branco fixo** por cima.
 * Medido em 2026-09-03: quatro dos seis tons não seguram 4,5:1 com branco (`--verde` dá 1,72), e no
 * tema escuro `--tinta-fraca` **vira clara** — branco sobre ela dá **1,58:1**. A cor era escolhida
 * "por contraste com branco" e ninguém tinha medido.
 *
 * Seis é o suficiente: mais tons e a lista vira um mosaico, que é ruído em vez de pista.
 */
const TOKENS = ["--azul", "--verde", "--roxo", "--azul-face", "--atencao", "--tinta-fraca"] as const;

/**
 * Escolhe o token pelo identificador.
 *
 * Soma de códigos, e não `hash` de verdade: o objetivo é ser **estável**, não distribuir bem. Um
 * telefone e outro do mesmo DDD caindo na mesma cor é aceitável; a mesma pessoa mudando de cor entre
 * duas telas não é.
 */
function tokenDoAvatar(chave: string): (typeof TOKENS)[number] {
  let soma = 0;
  for (let i = 0; i < chave.length; i += 1) soma += chave.charCodeAt(i);
  return TOKENS[soma % TOKENS.length]!;
}

/** A referência CSS da cor de identidade do contato, para quem só precisa dela como fundo. Serve a quem só precisa da cor. */
export function corDoAvatar(chave: string): string {
  return `var(${tokenDoAvatar(chave)})`;
}

/**
 * As cores LEGÍVEIS da inicial: a técnica da etiqueta (H91), aplicada à palheta.
 *
 * A etiqueta resolve exatamente este problema para a cor que o cliente escolhe — fundo é a cor
 * misturada com a superfície do tema, texto é a **mesma cor** aproximada do preto ou do branco até
 * passar 4,5:1, arredondado a 8 bits antes de comparar. Preserva a identidade (o verde continua
 * verde) e passa nos dois temas, provado em 216 tons. Não faz sentido inventar uma segunda técnica
 * para seis cores nossas quando a primeira já cobre qualquer cor.
 *
 * O valor do token é lido do `:root` em tempo de execução, como `superficiesDoTema()` faz — este
 * arquivo continua sem um único hexadecimal, e acompanha a palheta sem ninguém lembrar dele. As
 * DUAS variantes saem juntas e quem escolhe por tema é o CSS (`.avatar-inicial`, no `vidro.css`).
 *
 * Devolve `null` fora do navegador (teste em jsdom, sem folha de estilo) — e aí a inicial cai no
 * desenho antigo, `var()` de fundo com branco por cima, que é o único que dá para desenhar sem
 * saber o valor da cor.
 */
export function coresDoAvatar(chave: string): ParDeCores | null {
  const raiz = typeof document === "undefined" ? null : document.documentElement;
  if (!raiz) return null;
  const hex = getComputedStyle(raiz).getPropertyValue(tokenDoAvatar(chave)).trim();
  return hex ? coresDaEtiqueta(hex, superficiesDoTema()) : null;
}

/** A inicial. Do nome quando há; do identificador quando não. */
function inicialDe(nome: string): string {
  const limpo = nome.trim();
  if (!limpo) return "?";
  // Só letra: um nome que começa com `+55` daria "+", que não identifica ninguém.
  const letra = limpo.split("").find((c) => /\p{L}/u.test(c));
  return (letra ?? limpo.charAt(0)).toUpperCase();
}

export interface AvatarProps {
  readonly nome: string;
  /** Para a cor, e para o `alt`. O telefone entra aqui e **não** aparece na tela. */
  readonly chave: string;
  /** A URL da foto. Use `urlDaFotoDoContato(id)` — ver o cabeçalho. 404 cai no fallback sozinho. */
  readonly foto?: string | null;
  /** Sigla do canal, no canto — `WA`, `TG`. Ausente = sem badge. */
  readonly canal?: string | null;
  readonly tamanho?: "pequeno" | "medio" | "grande";
}

const MEDIDAS = {
  pequeno: { caixa: "h-8 w-8", texto: "text-xs", badge: "text-[8px]" },
  medio: { caixa: "h-10 w-10", texto: "text-sm", badge: "text-[9px]" },
  grande: { caixa: "h-12 w-12", texto: "text-base", badge: "text-[10px]" },
} as const;

/** A inicial sobre a cor de identidade — legível nos dois temas. Ver `coresDoAvatar`. */
function Inicial(props: { readonly chave: string; readonly classe: string; readonly children: string }) {
  const cores = coresDoAvatar(props.chave);
  const forma = `grid h-full w-full place-items-center rounded-full font-semibold ${props.classe}`;
  if (!cores) {
    return (
      <span
        aria-hidden="true"
        className={`${forma} text-white`}
        style={{ background: corDoAvatar(props.chave), boxShadow: "var(--sombra-interna)" }}
      >
        {props.children}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`${forma} avatar-inicial`}
      style={
        {
          "--avatar-fundo-claro": cores.claro.fundo,
          "--avatar-texto-claro": cores.claro.texto,
          "--avatar-fundo-escuro": cores.escuro.fundo,
          "--avatar-texto-escuro": cores.escuro.texto,
          boxShadow: "var(--sombra-interna)",
        } as React.CSSProperties
      }
    >
      {props.children}
    </span>
  );
}

export function Avatar(props: AvatarProps) {
  const m = MEDIDAS[props.tamanho ?? "medio"];
  // Foto que falha ao carregar cai no fallback. Sem isto, um `<img>` quebrado deixaria um ícone de
  // imagem partida na lista — pior que a inicial.
  const [falhou, setFalhou] = useState(false);
  const mostrarFoto = Boolean(props.foto) && !falhou;

  return (
    // `inline-block` NAO e' detalhe: `<span>` e' inline por padrao, e em inline a largura e a altura
    // sao ignoradas — o avatar virou uma barra fina e o badge absoluto escapou para fora. Foi o
    // primeiro defeito visivel deste componente na tela.
    <span className={`relative inline-block shrink-0 align-middle ${m.caixa}`}>
      {mostrarFoto ? (
        <img
          src={props.foto!}
          alt=""
          onError={() => setFalhou(true)}
          className="h-full w-full rounded-full object-cover"
          style={{ boxShadow: "var(--sombra-interna)" }}
        />
      ) : (
        <Inicial chave={props.chave} classe={m.texto}>
          {inicialDe(props.nome)}
        </Inicial>
      )}

      {props.canal ? (
        <span
          aria-label={`Canal: ${props.canal}`}
          /*
            `--papel` e nao `text-white`: o fundo e' `--tinta`, e ela VIRA com o tema — no escuro ela
            e' quase branca. Com branco fixo por cima, o badge do canal ficava com 1,25:1 no tema
            escuro, ou seja, invisivel. O par `--tinta` / `--papel` inverte junto, e passa nos dois.
            (Sem o valor hexadecimal escrito aqui: a catraca de palheta casa por texto, e ate' em
            comentario ela acusa cor crua — com razao, porque comentario envelhece.)
          */
          className={`absolute -bottom-0.5 -right-1 rounded px-1 font-semibold leading-tight text-[color:var(--papel)] ${m.badge}`}
          style={{ background: "var(--tinta)", boxShadow: "var(--sombra-interna)" }}
        >
          {props.canal}
        </span>
      ) : null}
    </span>
  );
}
